/**
 * Utility functions for consistent price and currency formatting
 */

/**
 * Formats a price value in Brazilian Real (BRL) with exactly 2 decimal places
 * @param value - The numeric value to format
 * @param precision - Number of decimal places to show (default: 4)
 * @param locale - Locale string (default: 'pt-BR')
 * @returns Formatted price string (e.g., "R$ 1.234,56")
 */
export function formatPrice(value: number, precision = 4, locale: string = 'pt-BR'): string {
  if (value === undefined || value === null || isNaN(value)) return '—';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(value);
}

/**
 * Formats a percentage value with exactly 2 decimal places
 * @param value - The percentage value to format
 * @param showSign - Whether to show + sign for positive values (default: true)
 * @param precision - Number of decimal places to show (default: 2)
 * @param locale - Locale string (default: 'pt-BR')
 * @returns Formatted percentage string (e.g., "+1,23%")
 */
export function formatPercent(
  value: number,
  showSign = true,
  precision = 2,
  locale: string = 'pt-BR',
): string {
  if (value === undefined || value === null || isNaN(value)) return '—';

  const sign = showSign && value > 0 ? '+' : '';
  const formatted = value.toLocaleString(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  return `${sign}${formatted}%`;
}

/**
 * Formats a number with thousands separator and 2 decimal places
 * @param value - The numeric value to format
 * @param precision - Number of decimal places to show (default: 4)
 * @param locale - Locale string (default: 'pt-BR')
 * @returns Formatted number string (e.g., "1.234,5621")
 */
export function formatNumber(value: number, precision = 4, locale: string = 'pt-BR'): string {
  if (value === undefined || value === null || isNaN(value)) return '—';

  return value.toLocaleString(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}
